import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Card from "react-bootstrap/Card"; // ✅ Added Card
import { FaMoneyBillWave, FaEdit } from "react-icons/fa";
import "./expense-info.css";

const INFRA_CATEGORIES = [
  "Main Room",
  "Bathroom",
  "Solar",
  "Masjid",
  "Main Gate",
];

const LAB_CATEGORIES = [
  "Masonry Work",
  "Construction Work",
  "Electrical Work",
  "Sanitary Work",
  "Maintenance Work",
  "Painting Work",
  "Rent",
  "Utilities",
  "Salaries",
  "Office Supplies",
  "Marketing",
  "Travel",
  "Software",
  "Equipment",
  "Miscellaneous",
];

const EditExpense = () => {
  const { expenseId } = useParams();
  const navigate = useNavigate();

  const [expenseType, setExpenseType] = useState("Lab");
  const [expense, setExpense] = useState({
    expenseDate: "",
    category: "",
    description: "",
    amount: "",
    paidTo: "",
    paymentMethod: "Cash",
    notes: "",
    billImage: "",
  });
  const [newBillImage, setNewBillImage] = useState(null);
  const [showAlert, setShowAlert] = useState(false);

  const categoryOptions =
    expenseType === "Infra" ? INFRA_CATEGORIES : LAB_CATEGORIES;

  const paymentMethods = ["Cash", "Bank Transfer", "Cheque", "Card", "Other"];

  useEffect(() => {
    const fetchExpense = async () => {
      try {
        const res = await axios.get(
          `https://backend.ems.hiradentallab.com/api/expenses/${expenseId}`
        );
        const data = res.data;
        if (data.expenseDate) {
          data.expenseDate = new Date(data.expenseDate)
            .toISOString()
            .split("T")[0];
        }
        setExpenseType(data.type === "Infra" ? "Infra" : "Lab");
        setExpense({
          ...data,
          billImage: data.billImage || "",
        });
      } catch (err) {
        console.error("Error fetching expense:", err);
      }
    };
    fetchExpense();
  }, [expenseId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setExpense((prev) => ({ ...prev, [name]: value }));
  };

  const handleTabChange = (tab) => {
    if (tab === expenseType) return;
    setExpenseType(tab);
    setExpense((prev) => ({ ...prev, category: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("type", expenseType);
      formData.append("expenseDate", expense.expenseDate);
      formData.append("category", expense.category);
      formData.append("description", expense.description);
      formData.append("amount", expense.amount);
      formData.append("paidTo", expense.paidTo);
      formData.append("paymentMethod", expense.paymentMethod);
      formData.append("notes", expense.notes);

      if (newBillImage) formData.append("billImage", newBillImage);

      await axios.put(
        `https://backend.ems.hiradentallab.com/api/expenses/update/${expenseId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setShowAlert(true);
      setTimeout(() => navigate("/expense-info"), 2000);
    } catch (err) {
      console.error("Error updating expense:", err);
    }
  };

  return (
    // ✅ Fixed Wrapper
    <div className="expense-page-wrap">

      {/* ✅ Fixed Titles */}
      <h1 className="expense-page-title">
        <FaEdit /> Edit Expense #{expenseId}
      </h1>
      <hr className="expense-divider" />

      <h1 className="expense-page-subtitle">
        <FaMoneyBillWave /> Expense Details
      </h1>
      <hr className="expense-divider" />

      {/* ✅ Wrapped Form in Card */}
      <Card className="expense-filter-card" style={{ padding: "24px" }}>
        <Form onSubmit={handleSubmit}>
          {showAlert && (
            <Alert variant="success" onClose={() => setShowAlert(false)} dismissible>
              Expense updated successfully!
            </Alert>
          )}

          {/* ============ TABS ============ */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "20px", borderBottom: "2px solid #e6eefc", paddingBottom: "0px", flexWrap: "wrap" }}>
            {["Infra", "Lab"].map((t) => {
              const isActive = expenseType === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => handleTabChange(t)}
                  style={{
                    padding: "10px 24px",
                    borderRadius: "6px 6px 0 0",
                    border: "1px solid #004aad",
                    borderBottom: isActive ? "2px solid #ffffff" : "1px solid #004aad",
                    marginBottom: "-2px",
                    background: isActive ? "#004aad" : "#ffffff",
                    color: isActive ? "#ffffff" : "#004aad",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {t}
                </button>
              );
            })}
          </div>

          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6} controlId="expenseDate">
              <Form.Label>Expense Date:</Form.Label>
              <Form.Control
                type="date"
                name="expenseDate"
                value={expense.expenseDate}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} controlId="category">
              <Form.Label>{expenseType} Category:</Form.Label>
              <Form.Control
                as="select"
                name="category"
                value={expense.category}
                onChange={handleChange}
                required
              >
                <option value="">--Select Category--</option>
                {categoryOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
                {expense.category && !categoryOptions.includes(expense.category) && (
                  <option value={expense.category}>{expense.category} (legacy)</option>
                )}
              </Form.Control>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6} controlId="description">
              <Form.Label>Description:</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={expense.description}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} controlId="amount">
              <Form.Label>Amount (PKR):</Form.Label>
              <Form.Control
                type="number"
                name="amount"
                value={expense.amount}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6} controlId="paidTo">
              <Form.Label>Paid To:</Form.Label>
              <Form.Control
                type="text"
                name="paidTo"
                value={expense.paidTo}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} controlId="paymentMethod">
              <Form.Label>Payment Method:</Form.Label>
              <Form.Control
                as="select"
                name="paymentMethod"
                value={expense.paymentMethod}
                onChange={handleChange}
              >
                {paymentMethods.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} xs={12} controlId="notes">
              <Form.Label>Notes:</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={expense.notes}
                onChange={handleChange}
              />
            </Form.Group>
          </Row>

          <Row className="mb-4">
            <Form.Group as={Col} xs={12} md={6} controlId="billImage">
              <Form.Label>Bill Image:</Form.Label>
              {expense.billImage && (
                <div className="mb-2">
                  <a
                    href={`https://backend.ems.hiradentallab.com/${expense.billImage}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Current Bill
                  </a>
                </div>
              )}
              <Form.Control
                type="file"
                accept=".png, .jpg, .jpeg, .pdf"
                onChange={(e) => setNewBillImage(e.target.files[0])}
              />
              <small className="text-muted">Leave empty to keep the current bill.</small>
            </Form.Group>
          </Row>

          <div style={{ display: "flex", gap: "10px" }}>
            <Button className="expense-add-btn" type="submit">
              Update Expense
            </Button>
            <Button variant="secondary" onClick={() => navigate("/expense-info")}>
              Cancel
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default EditExpense;